import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  FaTimes,
  FaPencilAlt,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaSave,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import { useAppContext } from '../Store/Store';
import RichTextEditor from './RichTextEditor';
import './Management.css';

/* ── types ────────────────────────────────────────────────────────────────── */
interface EventOption {
  id: number;
  name: string;
  code: string;
}
type PhaseType = 'commissioning' | 'survey';
type AnswerType = 'open' | 'single' | 'multi';

interface MarketTestConfig {
  id: number;
  phase_code: string;
  phase_name: string;
  indication: string;
  insightgpt_research: string;
  event_id: number | null;
  event_name: string | null;
  brand_id: number | null;
  brand_name: string | null;
  client_name: string | null;
  multianswer_selection: boolean;
  multianswer_allow_multiple: boolean;
  multianswer_options?: string[];
  multianswer_show_free_textarea: boolean;
}

const ANSWER_TYPE_META: Record<AnswerType, { label: string; cls: string }> = {
  open: { label: 'Open', cls: 'mgmt-badge mgmt-badge--secondary' },
  single: {
    label: 'Closed - Single answer',
    cls: 'mgmt-badge mgmt-badge--primary',
  },
  multi: {
    label: 'Closed - Multi answer',
    cls: 'mgmt-badge mgmt-badge--purple',
  },
};

function getAnswerType(
  c: Pick<
    MarketTestConfig,
    'multianswer_selection' | 'multianswer_allow_multiple'
  >
): AnswerType {
  if (!c.multianswer_selection) return 'open';
  return c.multianswer_allow_multiple ? 'multi' : 'single';
}

function answerTypeToFlags(type: AnswerType): {
  multianswer_selection: boolean;
  multianswer_allow_multiple: boolean;
} {
  if (type === 'single')
    return { multianswer_selection: true, multianswer_allow_multiple: false };
  if (type === 'multi')
    return { multianswer_selection: true, multianswer_allow_multiple: true };
  return { multianswer_selection: false, multianswer_allow_multiple: false };
}

interface PaginationInfo {
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

type SortKey = 'phase' | 'type' | 'event';
type SortDir = 'asc' | 'desc';

/* ── helpers ──────────────────────────────────────────────────────────────── */
function phaseTypeBadge(code: string) {
  if (code.startsWith('commissioning'))
    return (
      <span className="mgmt-badge mgmt-badge--warning">Commissioning</span>
    );
  return <span className="mgmt-badge mgmt-badge--info">Survey</span>;
}

/* ── component ────────────────────────────────────────────────────────────── */
const MarketTestConfigs: React.FC = () => {
  const { user, backendBaseUrl } = useAppContext();

  const [configs, setConfigs] = useState<MarketTestConfig[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);

  /* filters */
  const [events, setEvents] = useState<EventOption[]>([]);
  const [modalEvents, setModalEvents] = useState<EventOption[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [filterType, setFilterType] = useState<'' | PhaseType>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortKey | ''>('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  /* modal state */
  const [modalConfig, setModalConfig] = useState<MarketTestConfig | null>(null);
  const [editIndication, setEditIndication] = useState('');
  const [editResearch, setEditResearch] = useState('');
  const [editEventId, setEditEventId] = useState('');
  const [editAnswerType, setEditAnswerType] = useState<AnswerType>('open');
  const [editMultianswerOptions, setEditMultianswerOptions] = useState<
    string[]
  >([]);
  const [editFreeTextarea, setEditFreeTextarea] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retroactive, setRetroactive] = useState(false);
  const textareaRef = useRef<HTMLDivElement>(null);

  /* ── data fetching ── */
  const fetchEvents = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(
        `${backendBaseUrl}/employee/events/?user_id=${user.id}`
      );
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [backendBaseUrl, user?.id]);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (user?.id) params.append('user_id', String(user.id));
      if (selectedEventId) params.append('event_id', selectedEventId);
      if (searchQuery) params.append('q', searchQuery);
      if (filterType) params.append('type', filterType);
      if (sortBy) {
        params.append('sort', sortBy);
        params.append('dir', sortDir);
      }
      params.append('page', String(currentPage));
      params.append('page_size', String(pageSize));
      const res = await axios.get(
        `${backendBaseUrl}/employee/market-test-configs/?${params.toString()}`
      );
      setConfigs(res.data.configs);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [
    backendBaseUrl,
    user?.id,
    selectedEventId,
    searchQuery,
    filterType,
    sortBy,
    sortDir,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  /* ── modal helpers ── */
  const openModal = (cfg: MarketTestConfig) => {
    setModalConfig(cfg);
    setEditIndication(cfg.indication || '');
    setEditResearch(cfg.insightgpt_research || '');
    setEditEventId(cfg.event_id != null ? String(cfg.event_id) : '');
    setEditAnswerType(getAnswerType(cfg));
    setEditMultianswerOptions([...(cfg.multianswer_options ?? [])]);
    setEditFreeTextarea(cfg.multianswer_show_free_textarea);
    /* load the events this config can be (re)assigned to — scoped to its brand
       and fetched fresh each open so the dropdown never shows another brand's events */
    if (user?.id) {
      const params = new URLSearchParams({ user_id: String(user.id) });
      if (cfg.brand_id) params.append('brand_id', String(cfg.brand_id));
      axios
        .get(`${backendBaseUrl}/employee/events/?${params.toString()}`)
        .then(res => setModalEvents(res.data))
        .catch(() => setModalEvents([]));
    }
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const closeModal = () => {
    setModalConfig(null);
    setSaving(false);
    setRetroactive(false);
    setEditAnswerType('open');
    setEditMultianswerOptions([]);
    setEditFreeTextarea(false);
  };

  /* ── answer type change ── */
  /* moving to an open answer drops the closed-question data; single↔multi
     keeps the options the user already entered since the shape is identical */
  const handleAnswerTypeChange = (type: AnswerType) => {
    setEditAnswerType(prev => {
      if (prev === type) return prev;
      if (type === 'open') {
        setEditMultianswerOptions([]);
        setEditFreeTextarea(false);
      }
      return type;
    });
  };

  /* ── answer-type option helpers ── */
  const addOption = () => setEditMultianswerOptions(prev => [...prev, '']);
  const updateOption = (index: number, value: string) =>
    setEditMultianswerOptions(prev =>
      prev.map((o, i) => (i === index ? value : o))
    );
  const removeOption = (index: number) =>
    setEditMultianswerOptions(prev => prev.filter((_, i) => i !== index));

  const isClosed = editAnswerType === 'single' || editAnswerType === 'multi';
  const cleanedOptions = editMultianswerOptions
    .map(s => s.trim())
    .filter(Boolean);
  const optionsTooFew = isClosed && cleanedOptions.length < 2;
  const cannotSave = optionsTooFew;

  const handleSave = async () => {
    if (!modalConfig || cannotSave) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${backendBaseUrl}/employee/market-test-configs/${modalConfig.id}/`,
        {
          user_id: user?.id ?? null,
          indication: editIndication,
          insightgpt_research: editResearch,
          event_id: editEventId ? Number(editEventId) : null,
          multianswer_options: cleanedOptions,
          multianswer_show_free_textarea: editFreeTextarea,
          ...answerTypeToFlags(editAnswerType),
          retroactive,
        }
      );
      closeModal();
      fetchConfigs();
      if (retroactive && res.data?.retroactive_updated != null) {
        alert(
          `Config saved. ${res.data.retroactive_updated} product(s) updated retroactively.`
        );
      }
    } catch {
      alert('Failed to save config');
      setSaving(false);
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedEventId('');
    setFilterType('');
    setSortBy('');
    setSortDir('asc');
    setCurrentPage(1);
  };

  /* click a column header: asc → desc → unsorted */
  const handleSort = (key: SortKey) => {
    setCurrentPage(1);
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') setSortDir('desc');
    else {
      setSortBy('');
      setSortDir('asc');
    }
  };

  const sortableHeader = (
    key: SortKey,
    label: string,
    style?: React.CSSProperties
  ) => (
    <th
      className="mgmt-th--sortable"
      onClick={() => handleSort(key)}
      title={`Sort by ${label}`}
      style={style}
    >
      <span className="mgmt-th__sort">
        {label}
        {sortBy === key ? (
          sortDir === 'asc' ? (
            <FaSortUp size={11} />
          ) : (
            <FaSortDown size={11} />
          )
        ) : (
          <FaSort size={11} className="mgmt-th__sort-idle" />
        )}
      </span>
    </th>
  );

  /* configs are already filtered server-side (event/type/search), so the list
     and pagination.total_count stay in sync with the active filters */
  const visibleConfigs = configs;

  /* ── render ── */
  return (
    <>
      <h2 className="mgmt-page-title">Market Test Configs</h2>

      {/* Filters */}
      <div className="mgmt-filters">
        <div className="mgmt-filters__search">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="mgmt-filters__search-input"
            placeholder="Search by phase…"
            onKeyDown={e =>
              e.key === 'Enter' && (setCurrentPage(1), fetchConfigs())
            }
          />
          <button
            className="mgmt-filters__search-btn"
            onClick={() => {
              setCurrentPage(1);
              fetchConfigs();
            }}
          >
            🔍
          </button>
        </div>

        <div className="mgmt-filter-group">
          <label>Event</label>
          <select
            value={selectedEventId}
            onChange={e => {
              setSelectedEventId(e.target.value);
              setCurrentPage(1);
            }}
            className="mgmt-select"
            disabled={!events.length}
          >
            <option value="">All events</option>
            {events.map(e => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mgmt-filter-group">
          <label>Type</label>
          <select
            value={filterType}
            onChange={e => {
              setFilterType(e.target.value as '' | PhaseType);
              setCurrentPage(1);
            }}
            className="mgmt-select"
          >
            <option value="">All types</option>
            <option value="commissioning">Commissioning</option>
            <option value="survey">Survey</option>
          </select>
        </div>

        <div className="mgmt-filters__footer">
          <div className="mgmt-filters__footer-left" />
          <button
            onClick={resetFilters}
            className="mgmt-btn mgmt-btn--reset mgmt-btn--sm"
          >
            🧹 Reset filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mgmt-table-wrap">
        {loading ? (
          <div className="mgmt-table__empty">Loading…</div>
        ) : (
          <table className="mgmt-table">
            <thead>
              <tr>
                {sortableHeader('phase', 'Phase')}
                {sortableHeader('type', 'Type')}
                {sortableHeader('event', 'Event')}
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleConfigs.map(cfg => (
                <tr key={cfg.id}>
                  <td
                    style={{ fontWeight: 600, color: 'var(--mgmt-gray-900)' }}
                  >
                    <span className="cell-mono">{cfg.phase_code}</span>
                  </td>
                  <td>{phaseTypeBadge(cfg.phase_code)}</td>
                  <td>
                    {cfg.event_name ? (
                      <span className="mgmt-badge mgmt-badge--primary">
                        {cfg.event_name}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--mgmt-gray-400)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => openModal(cfg)}
                      className="mgmt-btn mgmt-btn--outline mgmt-btn--sm"
                    >
                      <FaPencilAlt size={11} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
              {visibleConfigs.length === 0 && (
                <tr>
                  <td colSpan={5} className="mgmt-table__empty">
                    <span
                      style={{
                        display: 'block',
                        marginBottom: '0.4rem',
                        fontSize: '1.5rem',
                      }}
                    >
                      🧪
                    </span>
                    No market test configs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {pagination && (
          <div className="mgmt-pagination">
            <div className="mgmt-pagination__info">
              <span className="mgmt-pagination__count">
                {pagination.total_count} config
                {pagination.total_count !== 1 ? 's' : ''}
              </span>
              <label className="mgmt-pagination__page-size">
                Rows
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[20, 50, 100].map(n => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div
              className="mgmt-pagination__pages"
              style={{
                visibility: pagination.total_pages > 1 ? 'visible' : 'hidden',
              }}
            >
              <button
                className="mgmt-pagination__btn"
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                <FaChevronLeft size={11} /> Prev
              </button>
              <span className="mgmt-pagination__page-label">
                {currentPage} / {pagination.total_pages}
              </span>
              <button
                className="mgmt-pagination__btn"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === pagination.total_pages}
              >
                Next <FaChevronRight size={11} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {modalConfig && (
        <div className="mgmt-modal-backdrop">
          <div className="mgmt-modal" role="dialog" aria-modal="true">
            <div className="mgmt-modal__header">
              <div>
                <h3 className="mgmt-modal__title">
                  {modalConfig.phase_name}
                  <span
                    className="cell-mono"
                    style={{
                      marginLeft: '0.6rem',
                      fontSize: '0.8em',
                      fontWeight: 400,
                    }}
                  >
                    {modalConfig.phase_code}
                  </span>
                </h3>
                <p className="mgmt-modal__subtitle">
                  {phaseTypeBadge(modalConfig.phase_code)}
                  {modalConfig.brand_name && (
                    <span
                      className="mgmt-badge mgmt-badge--secondary"
                      style={{ marginLeft: '0.4rem' }}
                    >
                      {modalConfig.brand_name}
                    </span>
                  )}
                </p>
              </div>
              <button
                className="mgmt-modal__close"
                onClick={closeModal}
                aria-label="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div className="mgmt-modal__body">
              {/* summary */}
              <div className="mgmt-modal__summary">
                <div className="mgmt-modal__summary-row">
                  <span className="mgmt-modal__summary-label">Phase</span>
                  <span className="cell-mono">{modalConfig.phase_code}</span>
                </div>
                <div className="mgmt-modal__summary-row">
                  <span className="mgmt-modal__summary-label">Type</span>
                  {phaseTypeBadge(modalConfig.phase_code)}
                </div>
                <div className="mgmt-modal__summary-row">
                  <span className="mgmt-modal__summary-label">Event</span>
                  <span>
                    {modalConfig.event_name ? (
                      <span className="mgmt-badge mgmt-badge--primary">
                        {modalConfig.event_name}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--mgmt-gray-400)' }}>—</span>
                    )}
                  </span>
                </div>
              </div>

              <div className="mgmt-modal__divider" />

              {/* indication editor */}
              <div className="mgmt-form-field">
                <label className="mgmt-form-label">Question text</label>
                <RichTextEditor
                  editorRef={textareaRef}
                  value={editIndication}
                  onChange={setEditIndication}
                  ariaLabel="Question text"
                  placeholder="Enter the question shown to users…"
                />
              </div>

              {/* answer type */}
              <div className="mgmt-form-field">
                <label className="mgmt-form-label">Answer type</label>
                <div className="mgmt-answer-type-group">
                  {(['open', 'single', 'multi'] as AnswerType[]).map(at => (
                    <button
                      key={at}
                      type="button"
                      className={`mgmt-answer-type-btn${editAnswerType === at ? ' mgmt-answer-type-btn--active' : ''}`}
                      onClick={() => handleAnswerTypeChange(at)}
                    >
                      <span
                        className={ANSWER_TYPE_META[at].cls}
                        style={{ pointerEvents: 'none' }}
                      >
                        {ANSWER_TYPE_META[at].label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {isClosed && (
                <div className="mgmt-form-field">
                  <label className="mgmt-form-label">
                    Answer options{' '}
                    <span className="mgmt-form-hint">
                      (
                      {editAnswerType === 'single'
                        ? 'the user picks one'
                        : 'the user can pick several'}
                      )
                    </span>
                  </label>
                  <div className="mgmt-options-editor">
                    {editMultianswerOptions.map((opt, i) => (
                      <div key={i} className="mgmt-option-row">
                        <span
                          className="mgmt-option-row__marker"
                          aria-hidden="true"
                        >
                          {editAnswerType === 'single' ? '○' : '☐'}
                        </span>
                        <input
                          type="text"
                          className="mgmt-form-control"
                          value={opt}
                          onChange={e => updateOption(i, e.target.value)}
                          placeholder={`Option ${i + 1}`}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (i === editMultianswerOptions.length - 1)
                                addOption();
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="mgmt-option-row__remove"
                          onClick={() => removeOption(i)}
                          title="Remove option"
                          aria-label={`Remove option ${i + 1}`}
                        >
                          <FaTimes size={11} />
                        </button>
                      </div>
                    ))}
                    {editMultianswerOptions.length === 0 && (
                      <p className="mgmt-form-hint" style={{ margin: 0 }}>
                        No options yet — add the choices users can select.
                      </p>
                    )}
                    <button
                      type="button"
                      className="mgmt-btn mgmt-btn--outline mgmt-btn--sm mgmt-option-add"
                      onClick={addOption}
                    >
                      <FaPlus size={10} /> Add option
                    </button>
                  </div>
                  {optionsTooFew && (
                    <p
                      className="mgmt-form-hint"
                      style={{ color: 'var(--mgmt-danger)' }}
                    >
                      Add at least two options to continue.
                    </p>
                  )}
                </div>
              )}

              <div className="mgmt-form-field">
                <label className="mgmt-form-label mgmt-form-label--checkbox">
                  <input
                    type="checkbox"
                    checked={editFreeTextarea}
                    onChange={e => setEditFreeTextarea(e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Show free-text answer field above the answer options
                </label>
              </div>

              {/* retroactive */}
              <div className="mgmt-form-field">
                <label className="mgmt-form-label">Apply changes to</label>
                <p
                  className="mgmt-form-hint"
                  style={{ marginBottom: '0.5rem' }}
                >
                  Choose whether the new question text and answer type should
                  also be applied to products already created, or only to those
                  created from now on.
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.4rem',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="retroactive"
                      checked={retroactive}
                      onChange={() => setRetroactive(true)}
                    />
                    <span>All questions</span>
                    <span className="mgmt-form-hint">
                      — also updates questions already created
                    </span>
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="retroactive"
                      checked={!retroactive}
                      onChange={() => setRetroactive(false)}
                    />
                    <span>Only new questions</span>
                    <span className="mgmt-form-hint">
                      — applies only to products created from now on
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mgmt-modal__footer">
              <button
                className="mgmt-btn mgmt-btn--outline"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="mgmt-btn mgmt-btn--primary"
                onClick={handleSave}
                disabled={saving || cannotSave}
              >
                <FaSave size={11} /> {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketTestConfigs;
